import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiRequestError, api } from '../lib/api.js';

interface Clip {
  key: string;
  kind: string;
  label: string;
  script: string;
  stage?: number;
  recorded: boolean;
  audited: boolean;
  source: string | null;
  url: string | null;
  confidence: 'high' | 'low' | 'human-only';
  listenFor: string;
  takes: number;
}

interface AudioView {
  clips: Clip[];
  totals: { required: number; recorded: number; audited: number };
  phonicsReadyThroughStage: number[];
}

interface VoiceView {
  configured: boolean;
  provider: string;
  voiceId: string;
  suggested: { id: string; label: string }[];
}

interface GenerateResult {
  generated: { key: string; confidence: string; takes: number }[];
  skipped: { key: string; reason: string; detail?: string }[];
}

/**
 * The audit bench.
 *
 * The parent's job here is to LISTEN, not to record - though recording is still
 * one tap away, because for several sounds it is the only honest option.
 *
 * Organised by phonics stage rather than by clip type, because a stage is the
 * unit that actually switches something on for a child. "Three more and Archie
 * can read" is a reason to keep going; "47 of 198 clips" is not.
 *
 * Nothing here can bulk-approve a phoneme. That friction is the safety: a wrong
 * phoneme is the one mistake that makes the whole reading suite worse than
 * useless, and only a human ear catches it.
 */
export function AdminAudio() {
  const qc = useQueryClient();

  const audio = useQuery({
    queryKey: ['admin', 'audio'],
    queryFn: () => api<AudioView>('/admin/audio'),
  });
  const voice = useQuery({
    queryKey: ['admin', 'audio', 'voice'],
    queryFn: () => api<VoiceView>('/admin/audio/voice'),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'audio'] });

  const clips = audio.data?.clips ?? [];
  const totals = audio.data?.totals;
  const ready = audio.data?.phonicsReadyThroughStage ?? [];
  const readyThrough = ready.length > 0 ? Math.max(...ready) : 0;

  const stages = [
    ...new Set(clips.map((c) => c.stage).filter((s): s is number => s !== undefined)),
  ].sort((a, b) => a - b);
  // Instructions carry no stage and are wanted from the very first session, so
  // they sit with stage 1 rather than in a bucket nobody opens.
  const unstaged = clips.filter((c) => c.stage === undefined);

  return (
    <div className="admin">
      <div className="admin-header">
        <div>
          <h1>Sounds</h1>
          <p className="muted">
            {totals
              ? `${totals.audited} of ${totals.required} checked · reading is on through stage ${readyThrough}`
              : audio.isPending
                ? 'Loading…'
                : 'Could not load the sounds.'}
          </p>
        </div>
        <a
          className="btn btn--outline"
          href="/api/admin/audio/script"
          target="_blank"
          rel="noreferrer"
        >
          Printable script
        </a>
      </div>

      <section className="card card--warning">
        <h2>Say the sound, not the letter</h2>
        <p className="muted">
          <strong>&ldquo;mmm&rdquo;</strong>, never <strong>&ldquo;muh&rdquo;</strong>. A child who
          learns the sound with an &ldquo;uh&rdquo; on the end will say &ldquo;muh-a-tuh&rdquo; for{' '}
          <em>mat</em> and never hear the word. It is the most common mistake in home phonics and it
          is hard to undo.
        </p>
        <p className="muted">
          Nothing reaches a child until you have listened and ticked it. Until then the activities
          that need it stay switched off &mdash; deliberately: no phonics beats wrong phonics.
        </p>
        {voice.data && !voice.data.configured && (
          <p className="muted">
            <strong>Generating isn&rsquo;t set up yet.</strong> Add <code>ELEVENLABS_API_KEY</code>{' '}
            to the server <code>.env</code> and restart, and the Generate buttons below come alive.
            Recording everything by hand works without it &mdash; that path is unchanged.
          </p>
        )}
      </section>

      {audio.isPending && <p className="muted">Loading…</p>}

      {/*
        A failed request used to leave "Loading…" on screen for ever, which is
        indistinguishable from a slow one and tells the parent nothing. The
        overwhelmingly likely cause is a session that has quietly lapsed, so
        say that and link to the fix rather than making them guess.
      */}
      {audio.isError && (
        <section className="card card--warning">
          <h2>Could not load the sounds</h2>
          <p className="muted">
            {audio.error instanceof ApiRequestError && audio.error.status === 401
              ? 'You are not signed in as a grown-up any more — sessions do lapse.'
              : audio.error instanceof ApiRequestError
                ? audio.error.body.message
                : 'The server did not answer.'}
          </p>
          <a className="btn" href="/parent">
            Sign in again
          </a>
        </section>
      )}

      {stages.map((stage) => (
        <StageSection
          key={stage}
          stage={stage}
          clips={[...clips.filter((c) => c.stage === stage), ...(stage === 1 ? unstaged : [])]}
          isReady={ready.includes(stage)}
          canGenerate={voice.data?.configured ?? false}
          onChanged={invalidate}
        />
      ))}
    </div>
  );
}

function StageSection({
  stage,
  clips,
  isReady,
  canGenerate,
  onChanged,
}: {
  stage: number;
  clips: Clip[];
  isReady: boolean;
  canGenerate: boolean;
  onChanged: () => void;
}) {
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  // Only phonemes gate a stage. Counting the instruction clips in "3 more to
  // go" would misstate how close the child actually is.
  const gating = clips.filter((c) => c.kind === 'phoneme');
  const remaining = gating.filter((c) => !c.audited).length;
  const needsAHuman = gating.filter((c) => c.confidence === 'human-only' && !c.recorded);

  const generate = useMutation({
    mutationFn: () =>
      api<GenerateResult>('/admin/audio/generate', { method: 'POST', body: { stage } }),
    onSuccess: (r) => {
      setResult(r);
      setProblem(null);
      onChanged();
    },
    onError: (e) =>
      setProblem(e instanceof ApiRequestError ? e.body.message : 'That did not work.'),
  });

  // Words and instructions can go in one tap: a whole word read wrong is
  // obvious on the first listen, and no child learns a broken rule from it.
  const bulkApprovable = clips.filter((c) => c.kind !== 'phoneme' && c.recorded && !c.audited);
  const bulkApprove = useMutation({
    mutationFn: async () => {
      for (const clip of bulkApprovable) {
        await api(`/admin/audio/${encodeURIComponent(clip.key)}/audited`, {
          method: 'POST',
          body: { audited: true },
        });
      }
    },
    onSuccess: onChanged,
  });

  return (
    <section className="card">
      <div className="child-head">
        <div>
          <h2>
            Stage {stage}{' '}
            {isReady ? (
              <span className="pill pill--ok">Reading is ON</span>
            ) : (
              <span className="pill pill--warn">
                {remaining} more {remaining === 1 ? 'sound' : 'sounds'} to check
              </span>
            )}
          </h2>
          <p className="muted">
            {isReady
              ? 'Every sound in this stage is checked, so the activities that need them are live.'
              : `The reading activities for stage ${stage} switch on when all ${gating.length} sounds here are ticked.`}
          </p>
          {!isReady && needsAHuman.length > 0 && (
            <p className="muted">
              <strong>{needsAHuman.length} of them need your voice</strong> &mdash;{' '}
              {needsAHuman.map((c) => c.label).join(', ')}. A machine cannot say a short vowel, or a
              sound like <em>t</em> or <em>p</em>, without putting an &ldquo;uh&rdquo; on the end.
              So those are recorded rather than generated. Ten seconds each.
            </p>
          )}
        </div>
      </div>

      <div className="selfie-actions" style={{ justifyContent: 'flex-start' }}>
        <button
          type="button"
          className="btn btn--outline"
          disabled={!canGenerate || generate.isPending}
          onClick={() => generate.mutate()}
          title={canGenerate ? undefined : 'No speech voice is set up on the server yet'}
        >
          {generate.isPending ? 'Generating…' : `Generate what it can for stage ${stage}`}
        </button>

        {bulkApprovable.length > 0 && (
          <button
            type="button"
            className="btn btn--quiet"
            disabled={bulkApprove.isPending}
            onClick={() => bulkApprove.mutate()}
          >
            {bulkApprove.isPending
              ? 'Approving…'
              : `Approve ${bulkApprovable.length} words & instructions`}
          </button>
        )}
      </div>

      {problem && <p className="error-note">{problem}</p>}
      {result && (
        <p className="muted">
          Made {result.generated.length}, skipped {result.skipped.length}
          {result.skipped.some((s) => s.reason === 'needs-a-human')
            ? ' — including the ones that need your voice.'
            : '.'}
        </p>
      )}

      {clips.map((clip) => (
        <ClipRow key={clip.key} clip={clip} onChanged={onChanged} canGenerate={canGenerate} />
      ))}
    </section>
  );
}

function ClipRow({
  clip,
  onChanged,
  canGenerate,
}: {
  clip: Clip;
  onChanged: () => void;
  canGenerate: boolean;
}) {
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const [recording, setRecording] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [take, setTake] = useState(1);

  const upload = useMutation({
    mutationFn: (dataUrl: string) =>
      api('/admin/audio', { method: 'POST', body: { key: clip.key, dataUrl, source: 'recorded' } }),
    onSuccess: onChanged,
  });

  const setAudited = useMutation({
    mutationFn: (audited: boolean) =>
      api(`/admin/audio/${encodeURIComponent(clip.key)}/audited`, {
        method: 'POST',
        body: { audited },
      }),
    onSuccess: onChanged,
  });

  const retake = useMutation({
    mutationFn: () =>
      api('/admin/audio/generate', { method: 'POST', body: { keys: [clip.key], force: true } }),
    onSuccess: onChanged,
  });

  const remove = useMutation({
    mutationFn: () => api(`/admin/audio/${encodeURIComponent(clip.key)}`, { method: 'DELETE' }),
    onSuccess: onChanged,
  });

  const start = async () => {
    setProblem(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = (e) => chunks.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: mr.mimeType.split(';')[0] });
        const reader = new FileReader();
        reader.onload = () => upload.mutate(String(reader.result));
        reader.readAsDataURL(blob);
      };
      mr.start();
      recorder.current = mr;
      setRecording(true);
    } catch {
      setProblem('This browser would not give us the microphone. You can upload a file instead.');
    }
  };

  const stop = () => {
    recorder.current?.stop();
    recorder.current = null;
    setRecording(false);
  };

  const chooseFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => upload.mutate(String(reader.result));
    reader.readAsDataURL(file);
  };

  const humanOnly = clip.confidence === 'human-only';
  const playKey = take === 1 ? clip.key : `${clip.key}.take${take}`;

  return (
    <div className="clip-row" data-audited={clip.audited}>
      <div>
        <strong>{clip.label}</strong>{' '}
        {clip.audited ? (
          <span className="pill pill--ok">Checked</span>
        ) : clip.recorded ? (
          <span className="pill pill--warn">Listen to this</span>
        ) : humanOnly ? (
          <span className="pill">Needs your voice</span>
        ) : (
          <span className="pill">Not made yet</span>
        )}
        {clip.confidence === 'low' && clip.recorded && !clip.audited && (
          <span className="pill pill--warn">Check this one carefully</span>
        )}
        {/* What to listen FOR. The difference between ticking and checking. */}
        <p className="muted">{clip.listenFor}</p>
      </div>

      <div className="selfie-actions" style={{ justifyContent: 'flex-start' }}>
        {clip.recorded && (
          <>
            {/* A parent may hear an unaudited clip; a child may not. */}
            <audio
              src={`/api/admin/audio/${encodeURIComponent(playKey)}/preview`}
              controls
              preload="none"
            />
            {clip.takes > 1 && (
              <span className="takes">
                {Array.from({ length: clip.takes }, (_, i) => i + 1).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="chip"
                    data-selected={take === t}
                    onClick={() => setTake(t)}
                  >
                    Take {t}
                  </button>
                ))}
              </span>
            )}
            <button
              type="button"
              className={clip.audited ? 'btn btn--outline' : 'btn'}
              onClick={() => setAudited.mutate(!clip.audited)}
            >
              {clip.audited ? 'Un-check' : 'I have listened — use it'}
            </button>
          </>
        )}

        {!recording ? (
          <button
            type="button"
            className={humanOnly && !clip.recorded ? 'btn' : 'btn btn--outline'}
            onClick={() => void start()}
            disabled={upload.isPending}
          >
            {clip.recorded ? 'Record over it' : 'Record it'}
          </button>
        ) : (
          <button type="button" className="btn btn--outline" onClick={stop}>
            Stop
          </button>
        )}

        {!humanOnly && canGenerate && (
          <button
            type="button"
            className="btn btn--quiet"
            disabled={retake.isPending}
            onClick={() => retake.mutate()}
          >
            {retake.isPending ? 'Re-taking…' : 'Re-take'}
          </button>
        )}

        <label className="btn btn--quiet">
          Upload
          <input
            type="file"
            accept="audio/*"
            className="is-visually-hidden"
            onChange={(e) => chooseFile(e.target.files?.[0])}
          />
        </label>

        {clip.recorded && (
          <button type="button" className="btn btn--quiet" onClick={() => remove.mutate()}>
            Delete
          </button>
        )}
      </div>

      {problem && <p className="error-note">{problem}</p>}
      {upload.isPending && <p className="muted">Saving…</p>}
    </div>
  );
}
